/**
 * paths — route patterns and their matching path builders, in one place.
 *
 * The pattern and the builder live side by side so a link can never point at a
 * route that no longer exists. `routes.tsx` consumes the PATTERN for its
 * `<Route path>`; link sites consume the BUILDER. Renaming a route therefore
 * updates both ends from a single edit, which is the property three
 * independently-hardcoded `/client-engagement/searches/${id}` template literals
 * did not have (`SearchesTable.tsx`, and now the two import-selection surfaces).
 *
 * Same consolidation `lib/thriveLinks.ts` performed for the OUTBOUND Thrive
 * deep links; this module is its in-app counterpart. Keep them distinct:
 * `thriveLinks` targets `nu.thrivetrm.com`, this targets our own router.
 *
 * The builder is DERIVED from the pattern by substitution, so renaming the route
 * really is a single edit. Declaring the two as independent string literals looked
 * equivalent and was not: it took two edits, and nothing would have caught a rename
 * applied to only one of them — which is precisely the drift this module exists to
 * prevent.
 *
 * Ids are percent-encoded. Thrive search ids are numeric today, so this is
 * defensive rather than load-bearing — but a builder that silently produces a
 * broken path for an id containing `/` or `?` is a worse failure than an escaped
 * one, and the encode costs nothing.
 */

/** Route pattern for Search Detail. Consumed by `routes.tsx`. */
export const SEARCH_DETAIL_ROUTE = "/client-engagement/searches/:searchId";

/** In-app path to a Thrive search's detail page. */
export function searchDetailPath(searchId: string): string {
  return SEARCH_DETAIL_ROUTE.replace(":searchId", encodeURIComponent(searchId));
}

/**
 * Route pattern for the standalone contact detail page (person side panel,
 * epic 2026-09-21). `:handle` is the `/in/<handle>` part of the person's
 * canonical LinkedIn URL, so the path mirrors `linkedin.com/in/…`.
 */
export const PERSON_PAGE_ROUTE = "/people/in/:handle";

/** In-app path to a person's contact detail page. */
export function personPagePath(handle: string): string {
  return PERSON_PAGE_ROUTE.replace(":handle", encodeURIComponent(handle));
}
